/* Axis arithmetic shared by every chart.
 *
 * The charts are hand-drawn SVG on a fixed viewBox with
 * preserveAspectRatio="none", so the plot area is always the same abstract
 * 1000 × H box and only the wrapper stretches. That keeps the maths here
 * unit-free and means no chart library ever has to agree with another. */

/** Round a maximum up to something a human would label an axis with. */
export function niceMax(v: number): number {
  if (!v || v <= 0) return 10
  const mag = Math.pow(10, Math.floor(Math.log10(v)))
  return Math.ceil(v / (mag / 2)) * (mag / 2)
}

/** A [lo, hi] band snapped to a round step, with `divisions` gaps. */
export function niceBand(lo: number, hi: number, divisions = 4): { lo: number; hi: number; step: number } {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo === hi) {
    return { lo: 0, hi: divisions, step: 1 }
  }
  const raw = (hi - lo) / divisions
  const mag = Math.pow(10, Math.floor(Math.log10(Math.abs(raw) || 1)))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10
  const low = Math.floor(lo / step) * step
  return { lo: low, hi: low + step * divisions, step }
}

/** Pad a range outward so lines never graze the frame. */
export function padded(lo: number, hi: number, frac = 0.12): [number, number] {
  const pad = (hi - lo) * frac || 1
  return [lo - pad, hi + pad]
}

export const ticks = (n: number): number[] => Array.from({ length: n + 1 }, (_, i) => i)

/** Evenly spaced values across a band, for axis labels. */
export function bandValues(lo: number, hi: number, divisions = 4): number[] {
  return ticks(divisions).map((i) => lo + ((hi - lo) * i) / divisions)
}

/** Map a value onto a y pixel, origin top-left as SVG wants it. */
export function yScale(lo: number, hi: number, height: number) {
  const span = hi - lo || 1
  return (v: number) => height - ((v - lo) / span) * height
}

export function xScale(lo: number, hi: number, width: number) {
  const span = hi - lo || 1
  return (v: number) => ((v - lo) / span) * width
}

/** Centre of the i-th of n equal columns. */
export function slotCentre(i: number, n: number, width = 1000): number {
  return (width / Math.max(1, n)) * (i + 0.5)
}

export const slotWidth = (n: number, width = 1000): number => width / Math.max(1, n)

export function polyline(points: [number, number][]): string {
  return points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
}

/** The chart palette, matching the reference mockups exactly. */
/* The six chart bars, as tokens rather than literals, so a theme switch
   repaints every series. Order matches the reference dashboard's own
   --c-bar assignments. */
export const PALETTE = [
  'var(--c-ink)', 'var(--c-bar3)', 'var(--c-bar1)',
  'var(--c-bar2)', 'var(--c-bar4)', 'var(--c-bar6)',
]
export const DASHES = ['0', '0', '0', '5 3', '5 3', '2 3']

export const seriesColor = (i: number) => PALETTE[i % PALETTE.length]
export const seriesDash = (i: number) => DASHES[i % DASHES.length]

/** Bar fill graded against the benchmark: met, or not.
 *
 * Two bands rather than three. The middle "nearly" band read as a third
 * verdict when there are only two — a day either earned the benchmark or it
 * did not — and it made the legend impossible to state plainly.
 *
 * Returns CSS variables rather than literals so the "missed" band can flip
 * light in the dark theme; a fixed dark navy sinks into the dark page on
 * exactly the days worth noticing. */
export function effFill(value: number, bench: number): string {
  return value >= bench ? 'var(--eff-strong)' : 'var(--eff-weak)'
}
